import React, { useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { sendSavedOneOnOneSettings } from 'constants/mixpanelHelper';
import { isNetchexLogin } from 'constants/netChexConstants';

import { Button } from 'uiToolkitV1/Button';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextLabel } from 'uiToolkitV1/Typography';

import { AGENDA_CONDITIONS_TYPE } from '../constants';
import { OneOnOneSettingsPageReactQueryKeys } from '../constants/OneOnOneSettingsPageReactQueryKeys';
import {
  createUpdateTemplateAgenda,
  deleteTemplateAgenda,
  deleteTemplateEvent,
  EventTemplateAgendaType,
} from '../features/AgendaPoints/services';
import { AdminAgendaType } from '../interfaces/AdminAgenda';
import { setOneOnOneSettingsPageData } from '../services';
import {
  resetIdsToBeDeleted,
  setSaveButtonIsDisabled,
  useEventIdsToBeDeleted,
  useSaveButtonIsDisabled,
} from '../slice/OneOnOneEventBasedAgendaSlice';

interface Props {
  saveButtonRef?: React.RefObject<HTMLButtonElement>;
}

const Footer = ({ saveButtonRef }: Props) => {
  const queryClient = useQueryClient();

  const eventIdsToBeDeleted = useEventIdsToBeDeleted();

  const dispatch = useDispatch();

  const toast = useToast();

  const [isSaving, setIsSaving] = useState(false);

  const isSaveButtonDisabled = useSaveButtonIsDisabled();

  const theme = useTheme();

  const saveAgendaHandler = async () => {
    const regularAgenda: AdminAgendaType[] = queryClient.getQueryData([
      OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda,
      'REGULAR',
    ]);
    const eventAgenda: EventTemplateAgendaType = queryClient.getQueryData([
      OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda,
      'EVENT_BASED',
    ]);

    const eventKeys = Object.keys(eventAgenda ?? {});

    try {
      const regularAgendaRes = await createUpdateTemplateAgenda({
        agendaCondition: 'REGULAR',
        agendaList: regularAgenda,
      });

      const eventAgendaRes = await Promise.all(
        eventKeys.map((eventKey: AGENDA_CONDITIONS_TYPE) =>
          createUpdateTemplateAgenda({
            agendaCondition: eventKey,
            agendaList: eventAgenda[eventKey],
          })
        )
      );
    } catch (error) {
      console.error('Error while saving agenda : ', error);
    }
  };

  const saveOneOnOneRatingEnabledHandler = async () => {
    const { isRatingEnabled, includeTranscriptionBot, isCarryForwardAgenda } = queryClient.getQueryData<{
      isRatingEnabled: boolean;
      includeTranscriptionBot: boolean;
      isCarryForwardAgenda: boolean;
    }>([
      OneOnOneSettingsPageReactQueryKeys.oneOnOneScoreEnabled,
      OneOnOneSettingsPageReactQueryKeys.includeTranscriptionBot,
    ]);
    try {
      const res = setOneOnOneSettingsPageData({
        isRatingEnabled,
        includeTranscriptionBot,
        isCarryForwardAgenda,
      });
    } catch (error) {
      console.error('Error while saving one on one rating enabled : ', error);
    }
  };

  const deleteAgendaHandler = async () => {
    const regularAgenda: AdminAgendaType[] = queryClient.getQueryData([
      OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda,
      'REGULAR',
    ]);
    const eventAgenda: EventTemplateAgendaType = queryClient.getQueryData([
      OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda,
      'EVENT_BASED',
    ]);

    const eventKeys = Object.keys(eventAgenda ?? {});

    const regularAgendaIdsToDelete = regularAgenda
      .map((agenda) => {
        if (agenda.isDeleted) {
          return agenda.id;
        }
        return null;
      })
      .filter((id) => id);
    const eventAgendaIdsToDelete = eventKeys
      .map((eventKey) =>
        eventAgenda[eventKey]
          .map((agenda) => {
            if (agenda.isDeleted) {
              return agenda.id;
            }
            return null;
          })
          .filter((id) => id)
      )
      .filter((id) => id);

    const allAgendaIdsToDelete = [...regularAgendaIdsToDelete, ...eventAgendaIdsToDelete].flat(Infinity);

    try {
      const res = await Promise.all(
        allAgendaIdsToDelete.map((agendaId) =>
          deleteTemplateAgenda({
            agendaId,
          })
        )
      );
    } catch (error) {
      console.error('Error deleting agenda, ids : ', allAgendaIdsToDelete, error);
    }
  };

  const deleteEventHandler = async () => {
    try {
      const res = await Promise.all(
        eventIdsToBeDeleted.map((eventId) =>
          deleteTemplateEvent({
            eventIdToBeDeleted: eventId,
          })
        )
      );
      // resting the ids to be deleted to empty array
      dispatch(resetIdsToBeDeleted());
    } catch (error) {
      console.error('Error deleting agenda, ids : ', eventIdsToBeDeleted, error);
    }
  };

  const onSaveHandler = async () => {
    try {
      setIsSaving(true);
      try {
        await saveAgendaHandler();
      } catch (err) {
        console.error('Error in saveAgendaHandler:', err);
      }

      try {
        await deleteAgendaHandler();
      } catch (err) {
        console.error('Error in deleteAgendaHandler:', err);
      }

      try {
        await saveOneOnOneRatingEnabledHandler();
      } catch (err) {
        console.error('Error in saveOneOnOneRatingEnabledHandler:', err);
      }

      try {
        await deleteEventHandler();
      } catch (err) {
        console.error('Error in deleteEventHandler:', err);
      }
      queryClient.invalidateQueries([OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda]);

      sendSavedOneOnOneSettings();

      dispatch(
        setSaveButtonIsDisabled({
          saveButtonIsDisabled: true,
        })
      );

      toast({
        title: 'Preferences have been successfully updated.',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Preferences could not be saved. Please try again later.',
        status: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isNewNavEnabled = checkNewNavEnabled();

  return (
    <PaperWithShadow
      style={{
        borderRadius: isNewNavEnabled ? '0px' : '8px',
        boxShadow: isNewNavEnabled ? '0px -1px 2px 0px #00000014' : undefined,
        background: isNewNavEnabled ? theme.colors.neutral.EBEFF3 : undefined,
        border: isNewNavEnabled ? 0 : undefined,
        position: isNetchexLogin ? 'fixed' : null,
        bottom: 0,
      }}
      mt={1}
      w="100%"
      h={isNetchexLogin ? null : '72px'}
      mx={!checkNewNavEnabled() ? 4 : 0}
      data-testid="one-on-one-settings-footer"
    >
      <Flex flexDir="column" width="100%">
        {isNetchexLogin && (
          <TextLabel bold pl="26px" py="12px">
            Note: Changes made here will apply across all companies in the organization.
          </TextLabel>
        )}
        <Flex justifyContent="space-between" alignItems="center" mt="5px">
          <Button ml="24px" onClick={() => null} variant="outlined">
            Cancel
          </Button>
          <Button mr="28px" ref={saveButtonRef} isDisabled={isSaving || isSaveButtonDisabled} onClick={onSaveHandler}>
            Save
          </Button>
        </Flex>
      </Flex>
    </PaperWithShadow>
  );
};

export default Footer;
